// apps/web/src/features/cargo/forms/fields.tsx
//
// Small RHF-aware field wrappers shared by the Cargo add/edit modals (#1232).
// They keep the modal bodies declarative and consistent (label + control +
// error) without re-deriving the FormRow plumbing per field. Generic over the
// form's value type so the `name` is type-checked against the form schema.

import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { FormRow, Input, Select, type SelectOption } from '../../../ui/index.js';

const NOOP = (): void => {};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export interface CargoTextFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  error?: string | undefined;
  /** Read-only renders the value but blocks edits (e.g. China payment for logist_ru). */
  readOnly?: boolean;
}

/** Free-text / numeric-like input bound through a Controller. */
export function CargoTextField<T extends FieldValues>({
  control,
  name,
  label,
  error,
  readOnly,
}: CargoTextFieldProps<T>) {
  return (
    <FormRow label={label} error={error}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Input
            value={asString(field.value)}
            onChange={readOnly ? NOOP : field.onChange}
            invalid={!!error}
            readOnly={readOnly ?? false}
          />
        )}
      />
    </FormRow>
  );
}

export interface CargoSelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  options: SelectOption[];
  /** Optional leading "unset" option for nullable selects. */
  placeholderOption?: SelectOption;
  error?: string | undefined;
  testId?: string;
}

/** Select bound through a Controller. */
export function CargoSelectField<T extends FieldValues>({
  control,
  name,
  label,
  options,
  placeholderOption,
  error,
  testId,
}: CargoSelectFieldProps<T>) {
  const merged = placeholderOption ? [placeholderOption, ...options] : options;
  return (
    <FormRow label={label} error={error}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <div data-testid={testId}>
            <Select
              value={asString(field.value)}
              onChange={field.onChange}
              options={merged}
              style={{ width: '100%' }}
            />
          </div>
        )}
      />
    </FormRow>
  );
}

export interface CargoDateFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  error?: string | undefined;
  testId?: string;
}

/** Native date input (date-only) bound through a Controller. */
export function CargoDateField<T extends FieldValues>({
  control,
  name,
  label,
  error,
  testId,
}: CargoDateFieldProps<T>) {
  return (
    <FormRow label={label} error={error}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <div data-testid={testId}>
            <Input
              type="date"
              value={asString(field.value)}
              onChange={field.onChange}
              invalid={!!error}
            />
          </div>
        )}
      />
    </FormRow>
  );
}
